import React from 'react';
import { ActivityIcon, ArrowLeftIcon, ArrowRightIcon, BookOpenIcon, LightbulbIcon, SmartphoneIcon, StarIcon, TargetIcon, TrendingUpIcon, UserIcon, UsersIcon } from 'lucide-react';
import { Button } from '../../components/Button';
import { MetricCard } from '../../components/MetricCard';
import { SectionHeading } from '../../components/SectionHeading';
export function AppCaseStudy() {
  return <div className="bg-cream" data-id="element-414">
      {/* Hero Section */}
      <section className="py-16 bg-white" data-id="element-415">
        <div className="container mx-auto px-6" data-id="element-416">
          <div className="max-w-4xl mx-auto" data-id="element-417">
            <h1 className="text-3xl md:text-4xl font-display font-bold text-navy-dark mb-6" data-id="element-418">
              iOS App Development
            </h1>
            <p className="text-lg text-navy mb-8" data-id="element-419">
              Building a dual-purpose customer platform that became FFF's most
              powerful retention tool.
            </p>
            <div className="mb-12" data-id="element-420">
              <img src="https://images.unsplash.com/photo-1605296867304-46d5465a13f1?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80" alt="iOS App" className="w-full h-auto rounded-lg shadow-lg" data-id="element-421" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-12 max-w-sm mx-auto" data-id="element-422">
              <MetricCard value="30%" label="Lower Churn Among App Users vs Web-Only Customers" data-id="element-423" />
            </div>
          </div>
        </div>
      </section>

      {/* Challenge Section */}
      <section className="py-16 bg-cream" data-id="element-424">
        <div className="container mx-auto px-6" data-id="element-425">
          <div className="max-w-4xl mx-auto" data-id="element-426">
            <SectionHeading title="The Challenge" subtitle="A high-value subscription with no meaningful digital touchpoint" data-id="element-427" />
            <div className="bg-white rounded-lg shadow-sm p-8 mb-12" data-id="element-428">
              <p className="text-navy mb-6" data-id="element-429">
                Fresh Fitness Food had built a loyal customer base around a
                premium meal delivery service, with customers spending over £100
                per order. But the business faced two distinct problems.
              </p>
              <div className="space-y-6" data-id="element-430">
                <div className="flex" data-id="element-431">
                  <div className="w-10 h-10 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark flex-shrink-0 mt-1" data-id="element-432">
                    <UserIcon size={20} data-id="element-433" />
                  </div>
                  <div data-id="element-434">
                    <h3 className="text-lg font-display font-semibold text-navy-dark mb-1" data-id="element-435">
                      Prospective Customers
                    </h3>
                    <p className="text-navy" data-id="element-436">
                      There was no low-commitment way to experience the FFF
                      proposition before subscribing. Conversion relied entirely
                      on the website and word of mouth.
                    </p>
                  </div>
                </div>
                <div className="flex" data-id="element-437">
                  <div className="w-10 h-10 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark flex-shrink-0 mt-1" data-id="element-438">
                    <SmartphoneIcon size={20} data-id="element-439" />
                  </div>
                  <div data-id="element-440">
                    <h3 className="text-lg font-display font-semibold text-navy-dark mb-1" data-id="element-441">
                      Existing Clients
                    </h3>
                    <p className="text-navy" data-id="element-442">
                      All account changes, including skipping a delivery,
                      updating dietary requirements and changing a delivery
                      address, required contacting customer services directly.
                      This created friction for customers and unnecessary
                      overhead for the team.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Bet Section */}
      <section className="py-16 bg-white" data-id="element-443">
        <div className="container mx-auto px-6" data-id="element-444">
          <div className="max-w-4xl mx-auto" data-id="element-445">
            <SectionHeading title="The Strategic Bet" subtitle="One app, two audiences" data-id="element-446" />
            <div className="bg-cream rounded-lg p-8 mb-8" data-id="element-447">
              <p className="text-navy mb-6" data-id="element-448">
                Rather than building two separate products, I led the decision
                to build a single iOS app that served two distinct user groups.
              </p>
              <div className="space-y-6" data-id="element-449">
                <div className="flex" data-id="element-450">
                  <div className="w-10 h-10 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark flex-shrink-0 mt-1" data-id="element-451">
                    <TargetIcon size={20} data-id="element-452" />
                  </div>
                  <div data-id="element-453">
                    <h3 className="text-lg font-display font-semibold text-navy-dark mb-1" data-id="element-454">
                      For Prospective Customers
                    </h3>
                    <p className="text-navy" data-id="element-455">
                      The app would offer a free nutrition tool: a way to engage
                      with their health goals before ever committing to a
                      subscription. The hypothesis was that demonstrating
                      genuine nutritional value upfront would build trust and
                      drive conversions to paying clients.
                    </p>
                  </div>
                </div>
                <div className="flex" data-id="element-456">
                  <div className="w-10 h-10 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark flex-shrink-0 mt-1" data-id="element-457">
                    <UsersIcon size={20} data-id="element-458" />
                  </div>
                  <div data-id="element-459">
                    <h3 className="text-lg font-display font-semibold text-navy-dark mb-1" data-id="element-460">
                      For Existing Clients
                    </h3>
                    <p className="text-navy" data-id="element-461">
                      The app would become a self-serve account management
                      platform, reducing friction and deepening engagement
                      between weekly deliveries.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Built Section */}
      <section className="py-16 bg-cream" data-id="element-462">
        <div className="container mx-auto px-6" data-id="element-463">
          <div className="max-w-4xl mx-auto" data-id="element-464">
            <SectionHeading title="What We Built" subtitle="A dual-tier platform serving free users and paying clients" data-id="element-465" />
            <div className="space-y-12" data-id="element-466">
              <div data-id="element-467">
                <div className="space-y-8" data-id="element-468">
                  {/* Free Tier: Nutrition Tracking */}
                  <div className="flex" data-id="element-469">
                    <div className="w-12 h-12 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark flex-shrink-0 mt-1" data-id="element-470">
                      <ActivityIcon size={24} data-id="element-471" />
                    </div>
                    <div data-id="element-472">
                      <h4 className="text-lg font-medium text-navy-dark mb-2" data-id="element-473">
                        Free Tier: Nutrition Tracking
                      </h4>
                      <div className="bg-white rounded-lg p-5" data-id="element-474">
                        <ul className="space-y-2" data-id="element-475">
                          <li className="flex" data-id="element-476">
                            <span className="text-coral-dark mr-3" data-id="element-477">•</span>
                            <span className="text-navy" data-id="element-478">
                              Biometric-based calorie and macro calculator
                              tailored to the user's goal
                            </span>
                          </li>
                          <li className="flex" data-id="element-479">
                            <span className="text-coral-dark mr-3" data-id="element-480">•</span>
                            <span className="text-navy" data-id="element-481">
                              Meal logging to track daily food intake
                            </span>
                          </li>
                          <li className="flex" data-id="element-482">
                            <span className="text-coral-dark mr-3" data-id="element-483">•</span>
                            <span className="text-navy" data-id="element-484">
                              Integration with Apple Health, bringing in
                              activity data from Apple Watch, Fitbit and Garmin
                            </span>
                          </li>
                          <li className="flex" data-id="element-485">
                            <span className="text-coral-dark mr-3" data-id="element-486">•</span>
                            <span className="text-navy" data-id="element-487">
                              Gamified progress tracking: two concentric rings
                              showing daily calorie intake vs expenditure, with
                              a celebration animation when targets are hit
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Client Tier: Order Management */}
                  <div className="flex" data-id="element-488">
                    <div className="w-12 h-12 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark flex-shrink-0 mt-1" data-id="element-489">
                      <SmartphoneIcon size={24} data-id="element-490" />
                    </div>
                    <div data-id="element-491">
                      <h4 className="text-lg font-medium text-navy-dark mb-2" data-id="element-492">
                        Client Tier: Order Management
                      </h4>
                      <div className="bg-white rounded-lg p-5" data-id="element-493">
                        <ul className="space-y-2" data-id="element-494">
                          <li className="flex" data-id="element-495">
                            <span className="text-coral-dark mr-3" data-id="element-496">•</span>
                            <span className="text-navy" data-id="element-497">
                              View upcoming menu and scheduled deliveries
                            </span>
                          </li>
                          <li className="flex" data-id="element-498">
                            <span className="text-coral-dark mr-3" data-id="element-499">•</span>
                            <span className="text-navy" data-id="element-500">
                              Skip deliveries and manage subscription
                              preferences
                            </span>
                          </li>
                          <li className="flex" data-id="element-501">
                            <span className="text-coral-dark mr-3" data-id="element-502">•</span>
                            <span className="text-navy" data-id="element-503">
                              Update dietary requirements, allergies and
                              exclusions without contacting customer services
                            </span>
                          </li>
                          <li className="flex" data-id="element-504">
                            <span className="text-coral-dark mr-3" data-id="element-505">•</span>
                            <span className="text-navy" data-id="element-506">
                              Reorder previous meals
                            </span>
                          </li>
                          <li className="flex" data-id="element-507">
                            <span className="text-coral-dark mr-3" data-id="element-508">•</span>
                            <span className="text-navy" data-id="element-509">
                              Log food eaten outside the FFF plan to track total
                              daily intake
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Meal Feedback */}
                  <div className="flex" data-id="element-510">
                    <div className="w-12 h-12 bg-sage-light rounded-full flex items-center justify-center mr-4 text-sage-dark flex-shrink-0 mt-1" data-id="element-511">
                      <StarIcon size={24} data-id="element-512" />
                    </div>
                    <div data-id="element-513">
                      <h4 className="text-lg font-medium text-navy-dark mb-2" data-id="element-514">
                        Meal Feedback
                      </h4>
                      <div className="bg-white rounded-lg p-5" data-id="element-515">
                        <ul className="space-y-2" data-id="element-516">
                          <li className="flex" data-id="element-517">
                            <span className="text-coral-dark mr-3" data-id="element-518">•</span>
                            <span className="text-navy" data-id="element-519">
                              Rate individual meals after each delivery
                            </span>
                          </li>
                          <li className="flex" data-id="element-520">
                            <span className="text-coral-dark mr-3" data-id="element-521">•</span>
                            <span className="text-navy" data-id="element-522">
                              Ratings fed directly into menu planning and future
                              recommendations
                            </span>
                          </li>
                          <li className="flex" data-id="element-523">
                            <span className="text-coral-dark mr-3" data-id="element-524">•</span>
                            <span className="text-navy" data-id="element-525">
                              Gave the product team a consistent, structured
                              source of customer feedback at scale
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Happened Section */}
      <section className="py-16 bg-white" data-id="element-526">
        <div className="container mx-auto px-6" data-id="element-527">
          <div className="max-w-4xl mx-auto" data-id="element-528">
            <SectionHeading title="What Happened" subtitle="An honest assessment" data-id="element-529" />
            <div className="bg-cream rounded-lg p-8" data-id="element-530">
              <p className="text-navy mb-6" data-id="element-531">
                The acquisition hypothesis did not perform as expected. Whilst
                the free nutrition tool attracted users, converting them to
                paying FFF customers proved more difficult than anticipated. In
                hindsight, this is understandable: at over £100 per order, FFF
                is a considered purchase. A free app can demonstrate value, but
                it cannot replace the trust-building that drives a high-value
                subscription decision.
              </p>
              <p className="text-navy" data-id="element-532">
                Where the app did deliver was retention. Customers who used the
                app were 30% less likely to churn compared to those who managed
                their account through the web portal only. The self-serve
                functionality also meaningfully reduced inbound customer service
                contacts, freeing up the team to focus on higher-value
                interactions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why It Mattered Section */}
      <section className="py-16 bg-cream" data-id="element-533">
        <div className="container mx-auto px-6" data-id="element-534">
          <div className="max-w-4xl mx-auto" data-id="element-535">
            <SectionHeading title="Why It Mattered" subtitle="Retention as the primary revenue lever" data-id="element-536" />
            <div className="bg-white rounded-lg shadow-sm p-8 mb-8" data-id="element-537">
              <p className="text-navy mb-6" data-id="element-538">
                For a subscription business with high order values, retention
                has a disproportionate impact on lifetime value. A 30% reduction
                in churn among app users represented a significant improvement
                to revenue per customer.
              </p>
              <p className="text-navy mb-6" data-id="element-539">
                The product also demonstrated that engagement between deliveries
                was a genuine differentiator. Customers who tracked their meals,
                connected their activity data and rated their food had a
                meaningfully different relationship with FFF than those who
                simply received a weekly box.
              </p>
              <p className="text-navy" data-id="element-540">
                Beyond the numbers, the app repositioned FFF as a technology-led
                wellness platform rather than a food delivery company, which
                supported the broader brand strategy and made the business more
                defensible in a competitive market.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What I Learnt Section */}
      <section className="py-16 bg-white" data-id="element-541">
        <div className="container mx-auto px-6" data-id="element-542">
          <div className="max-w-4xl mx-auto" data-id="element-543">
            <SectionHeading title="What I Learnt" subtitle="Product leadership lessons" data-id="element-544" />
            <div className="bg-cream rounded-lg p-8" data-id="element-545">
              <ul className="space-y-4" data-id="element-546">
                <li className="flex" data-id="element-547">
                  <span className="text-coral-dark mr-3" data-id="element-548">•</span>
                  <span className="text-navy" data-id="element-549">
                    The importance of validating acquisition assumptions early,
                    particularly for high-consideration purchases where trust is
                    the primary conversion barrier
                  </span>
                </li>
                <li className="flex" data-id="element-550">
                  <span className="text-coral-dark mr-3" data-id="element-551">•</span>
                  <span className="text-navy" data-id="element-552">
                    How to balance the needs of two distinct user groups within
                    a single product without compromising the experience for
                    either
                  </span>
                </li>
                <li className="flex" data-id="element-553">
                  <span className="text-coral-dark mr-3" data-id="element-554">•</span>
                  <span className="text-navy" data-id="element-555">
                    That honest product evaluation, including acknowledging when
                    a hypothesis does not land, is more valuable than
                    retrofitting a success narrative
                  </span>
                </li>
                <li className="flex" data-id="element-556">
                  <span className="text-coral-dark mr-3" data-id="element-557">•</span>
                  <span className="text-navy" data-id="element-558">
                    Self-serve functionality, whilst unglamorous, can deliver
                    meaningful business impact by reducing operational overhead
                    and improving customer satisfaction simultaneously
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Section */}
      <section className="py-12 bg-white" data-id="element-559">
        <div className="container mx-auto px-6" data-id="element-560">
          <div className="max-w-4xl mx-auto flex justify-between" data-id="element-561">
            <Button href="/case-study/acquisition" variant="outline" data-id="element-562">
              <ArrowLeftIcon size={16} className="mr-2" data-id="element-563" />
              Previous Case Study
            </Button>
            <Button href="/contact" data-id="element-564">
              Contact Me
              <ArrowRightIcon size={16} className="ml-2" data-id="element-565" />
            </Button>
          </div>
        </div>
      </section>
    </div>;
}